import {createContext, ReactNode, useState} from "react";
import {ReactElement} from "react";

import {getAdherentInfo} from "@/Api/adherent";
import {getCampagnes} from "@/Api/campagne";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {AppContext as AppContextType} from "@/ts/appContext";
import {OptionNameValue} from "@/ts/formulaire";
import {CampagneKpi} from "@/ts/kpi";

import {AppContext} from "./AppContext";

type StatutCampagne = "EnCours" | "Clôture" | "AVenir" | "Tous";

export type UserParamContextType = {
  currentCampagne: OptionNameValue;
  currentContremarque: OptionNameValue;
  handleSetCurrentCampagne: ({
    libelle,
    value,
  }: {
    libelle: string;
    value: string;
  }) => void;
  handleSetCurrentContremarque: ({
    libelle,
    value,
  }: {
    libelle: string;
    value: string;
  }) => void;
  allCampagnesInAllStatus: CampagneKpi[] | undefined;
  allCampagnes: OptionNameValue[] | undefined;
  handleFilterCampagnesByStatus: (statutCampagne: StatutCampagne) => void;
  fetchAllCampagnes: (statutCampagne: StatutCampagne) => Promise<void>;
  allContremarques: OptionNameValue[] | undefined;
  fetchAllContremarques: () => Promise<void>;
};

export const UserParamContext = createContext<UserParamContextType | null>(
  null
);

// permet de gérer les paramètres de l'utilisateur (campagne et contremarque)
// qui sont séléctionner dans le module select campagne et contremarque
export function UserParamContextProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const {API, userType} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );

  const [allCampagnesInAllStatus, setAllCampagnesInAllStatus] = useState<
    CampagneKpi[] | undefined
  >(undefined);

  // contient tout les campagnes existant (au statut actuel) pour les mettre dans le select
  const [allCampagnes, setAllCampagnes] = useState<
    OptionNameValue[] | undefined
  >(undefined);

  const [allContremarques, setAllContremarques] = useState<
    OptionNameValue[] | undefined
  >(undefined);

  // id de la campagne sélectionnée
  const [currentCampagne, setCurrentCampagne] = useState<OptionNameValue>({
    libelle: localStorage.getItem("campagne_libelle") || "",
    value: localStorage.getItem("campagne_value") || "",
  });

  // id de la contremarque sélectionnée
  const [currentContremarque, setCurrentContremarque] =
    useState<OptionNameValue>({
      libelle: localStorage.getItem("contremarque_libelle") || "",
      value: localStorage.getItem("contremarque_value") || "",
    });

  // fonction pour définir l'id de la campagne sélectionnée
  const handleSetCurrentCampagne = ({
    libelle,
    value,
  }: {
    libelle: string;
    value: string;
  }): void => {
    if (value) {
      localStorage.setItem("campagne_libelle", libelle);
      localStorage.setItem("campagne_value", value);
      setCurrentCampagne({libelle, value});
    }
  };

  // fonction pour définir l'id de la contremarque sélectionnée
  const handleSetCurrentContremarque = ({
    libelle,
    value,
  }: {
    libelle: string;
    value: string;
  }): void => {
    if (value) {
      localStorage.setItem("contremarque_libelle", libelle);
      localStorage.setItem("contremarque_value", value);
      setCurrentContremarque({libelle, value});
    }
  };

  const filterCampagnesByStatus = (
    campagnes: CampagneKpi[],
    statutCampagne: "EnCours" | "Clôture" | "AVenir" | "Tous"
  ): void => {
    const now = new Date();
    let campagneToUse: CampagneKpi[] = [];
    if (campagnes) {
      if (statutCampagne === "EnCours") {
        // Prend que les campagne en cours
        campagneToUse = campagnes?.filter(campagne => {
          return new Date(campagne.fin) > now && new Date(campagne.debut) < now;
        });
      } else if (statutCampagne === "Clôture") {
        campagneToUse = campagnes?.filter(campagne => {
          return new Date(campagne.fin) < now;
        });
      } else if (statutCampagne === "AVenir") {
        campagneToUse = campagnes?.filter(campagne => {
          return new Date(campagne.debut) > now;
        });
      } else if (statutCampagne === "Tous") {
        campagneToUse = campagnes;
      }
    }

    // Format les campagnes pour qu'ils soit utilisable dans le select
    const formattedCampagnes: OptionNameValue[] = campagneToUse.map(
      campagne => ({
        libelle: campagne.nom,
        value: campagne.campagne_id?.toString() || "",
      })
    );
    setAllCampagnes(formattedCampagnes);

    // si on a déjà une campagne dans le local storage et qu'elle est dans la liste de campagne que l'on a récupéré on la prend
    if (
      localStorage.getItem("campagne_value") !== undefined &&
      campagneToUse.find(
        campagne =>
          campagne.campagne_id?.toString() ===
          localStorage.getItem("campagne_value")
      ) !== undefined
    ) {
      setCurrentCampagne({
        libelle: localStorage.getItem("campagne_libelle") || "",
        value: localStorage.getItem("campagne_value") || "",
      });
    }
    // sinon on prend la première campagne en cours
    else if (formattedCampagnes.length > 0) {
      setCurrentCampagne(formattedCampagnes[0]);
      localStorage.setItem("campagne_value", formattedCampagnes[0].value);
      localStorage.setItem(
        "campagne_libelle",
        formattedCampagnes[0].libelle.toString()
      );
    }
  };

  const handleFilterCampagnesByStatus = (
    statutCampagne: StatutCampagne
  ): void => {
    if (allCampagnesInAllStatus) {
      filterCampagnesByStatus(allCampagnesInAllStatus, statutCampagne);
    }
  };
  // fonction pour récupérer toutes les campagnes
  const fetchAllCampagnes = async (
    statutCampagne: "EnCours" | "Clôture" | "AVenir" | "Tous"
  ): Promise<void> => {
    const {campagnes} = await getCampagnes(API, userType);
    // si on n'a pas de campagne, on ne fait rien
    if (campagnes.length === 0 || !campagnes) {
      setAllCampagnes([]);
      localStorage.removeItem("campagne_value");
      localStorage.removeItem("campagne_libelle");
      return;
    }
    setAllCampagnesInAllStatus(campagnes);
    filterCampagnesByStatus(campagnes, statutCampagne);
  };

  // fonction pour récupérer toutes les contremarques
  const fetchAllContremarques = async (): Promise<void> => {
    const adherentInfo = await getAdherentInfo(API);
    const formattedContremarques: OptionNameValue[] =
      adherentInfo.contremarques.map((contremarque: string) => ({
        libelle: contremarque,
        value: contremarque || "",
      }));
    if (
      localStorage.getItem("contremarque_value") !== undefined &&
      adherentInfo.contremarques.find(
        (contremarque: string) =>
          contremarque.toString() === localStorage.getItem("contremarque_value")
      )
    ) {
      console.log("contremarques", adherentInfo.contremarques);
      console.log(
        "contremarque trouvée dans le local storage",
        adherentInfo.contremarques.find(
          (contremarque: string) =>
            contremarque.toString() ===
            localStorage.getItem("contremarque_value")
        )
      );
      console.log(
        "contremarque trouvée dans le local storage",
        localStorage.getItem("contremarque_value")
      );
      setCurrentContremarque({
        libelle: localStorage.getItem("contremarque_libelle") || "",
        value: localStorage.getItem("contremarque_value") || "",
      });
    } else {
      const findIndex = [...formattedContremarques].findIndex(
        contremarque => contremarque.value === adherentInfo.contremarque_choisie
      );
      if (findIndex !== -1) {
        setCurrentContremarque(formattedContremarques[findIndex]);
        localStorage.setItem(
          "contremarque_value",
          formattedContremarques[findIndex].value
        );
        localStorage.setItem(
          "contremarque_libelle",
          formattedContremarques[findIndex].libelle.toString()
        );
      } else {
        setCurrentContremarque(formattedContremarques[0]);
        localStorage.setItem(
          "contremarque_value",
          formattedContremarques[0].value
        );
        localStorage.setItem(
          "contremarque_libelle",
          formattedContremarques[0].libelle.toString()
        );
      }
    }
    setAllContremarques(formattedContremarques);
  };

  return (
    <UserParamContext.Provider
      value={{
        currentCampagne,
        handleSetCurrentCampagne,
        currentContremarque,
        handleSetCurrentContremarque,
        allCampagnesInAllStatus,
        allCampagnes,
        fetchAllCampagnes,
        allContremarques,
        fetchAllContremarques,
        handleFilterCampagnesByStatus,
      }}>
      {children}
    </UserParamContext.Provider>
  );
}
